import { useState } from 'react'

import { hangAngle, oscStep, polygonCentroid, type Точка } from '../../lib/physics'
import { Choice, Predict, useFrames } from './common'

/**
 * Где центр тяжести (урок 33): пластину подвешивают за разные точки — отвес каждый раз
 * проходит через одну и ту же точку. У Г-образной пластины она оказывается вне пластины.
 */

interface Фигура {
  имя: string
  точки: Точка[]
  отверстия: Точка[]
  вопрос: string
  ответы: string[]
  верный: number
  пояснение: string
}

const ФИГУРЫ: Фигура[] = [
  {
    имя: 'Стержень',
    точки: [[0, 0], [200, 0], [200, 16], [0, 16]],
    отверстия: [[8, 8], [60, 8], [192, 8]],
    вопрос: 'Где центр тяжести однородного стержня?',
    ответы: ['у тяжёлого конца', 'в середине', 'у точки подвеса'],
    верный: 1,
    пояснение: 'Стержень однородный — его половины одинаково тяжёлые, центр тяжести в середине.',
  },
  {
    имя: 'Прямоугольник',
    точки: [[0, 0], [150, 0], [150, 90], [0, 90]],
    отверстия: [[10, 10], [140, 10], [75, 80], [10, 60]],
    вопрос: 'Где центр тяжести прямоугольной пластины?',
    ответы: ['в углу', 'на пересечении диагоналей', 'посередине верхней стороны'],
    верный: 1,
    пояснение: 'Отвесы пересекаются там же, где диагонали, — в центре пластины.',
  },
  {
    имя: 'Треугольник',
    точки: [[0, 0], [170, 0], [40, 140]],
    отверстия: [[12, 6], [158, 6], [42, 128]],
    вопрос: 'Где центр тяжести треугольной пластины?',
    ответы: ['в центре вписанной окружности', 'на пересечении медиан', 'у прямого угла'],
    верный: 1,
    пояснение: 'Отвес из вершины идёт по медиане; три медианы пересекаются в центре тяжести.',
  },
  {
    имя: 'Уголок',
    точки: [[0, 0], [140, 0], [140, 40], [40, 40], [40, 140], [0, 140]],
    отверстия: [[130, 20], [20, 130], [10, 10]],
    вопрос: 'Где центр тяжести Г-образной пластины?',
    ответы: ['на самой пластине', 'вне пластины, в вырезе', 'в углу сгиба'],
    верный: 1,
    пояснение: 'Центр тяжести не обязан лежать на теле: у уголка он в пустоте между полками.',
  },
]

const ПОДВЕС: Точка = [180, 34]

export default function CenterLab() {
  const [n, setN] = useState(3)
  const [отверстие, setОтверстие] = useState<number | null>(null)
  const [отвесы, setОтвесы] = useState<number[]>([])
  const [угол, setУгол] = useState({ a: 0, w: 0 })

  const ф = ФИГУРЫ[n]
  const цт = polygonCentroid(ф.точки)
  const центрФигуры: Точка = цт
  const подвес = отверстие === null ? null : ф.отверстия[отверстие]
  const цель = подвес ? hangAngle(подвес, цт) : 0

  useFrames((dt) => {
    if (!подвес) return
    setУгол((s) => {
      let { a, w } = s
      for (let i = 0; i < 4; i++) [a, w] = oscStep(a, w, цель, 4, 0.15, dt / 4)
      return { a, w }
    })
  })

  const подвесить = (i: number) => {
    setОтверстие(i)
    setОтвесы((x) => (x.includes(i) ? x : [...x, i]))
    // пластина «отпущена» из прежнего положения и качается к новому равновесию
    setУгол((s) => ({ a: s.a, w: 0 }))
  }

  const сменить = (i: number) => {
    setN(i)
    setОтверстие(null)
    setОтвесы([])
    setУгол({ a: 0, w: 0 })
  }

  // пластина лежит (не подвешена) — рисуем её с центром под точкой подвеса
  const опорная: Точка = подвес ?? [центрФигуры[0], центрФигуры[1] - 60]
  const градусы = (угол.a * 180) / Math.PI
  const преобразование = `translate(${ПОДВЕС[0]} ${ПОДВЕС[1] + (подвес ? 0 : 60)}) rotate(${градусы}) translate(${-опорная[0]} ${-опорная[1]})`
  const точки = ф.точки.map((p) => p.join(',')).join(' ')
  const найден = отвесы.length >= 2

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 300" role="img" aria-label="Пластина, подвешенная за отверстие">
        <line x1={150} y1={ПОДВЕС[1]} x2={210} y2={ПОДВЕС[1]} className="ink-muted" strokeWidth={4} />
        <g transform={преобразование}>
          <polygon points={точки} className="fill-body ink" strokeWidth={1.5} />
          {/* следы отвесов: линия из точки подвеса через центр тяжести, в координатах пластины */}
          {отвесы.map((i) => {
            const p = ф.отверстия[i]
            const dx = цт[0] - p[0]
            const dy = цт[1] - p[1]
            const len = Math.hypot(dx, dy) || 1
            return (
              <line
                key={i}
                x1={p[0]} y1={p[1]} x2={p[0] + (dx / len) * 320} y2={p[1] + (dy / len) * 320}
                className="ink-heavy" strokeDasharray="5 4" strokeWidth={1.5}
              />
            )
          })}
          {ф.отверстия.map((p, i) => (
            <circle
              key={i}
              cx={p[0]} cy={p[1]} r={6}
              className={i === отверстие ? 'fill-heavy lab-hole' : 'fill-paper ink lab-hole'}
              onClick={() => подвесить(i)}
            />
          ))}
          {найден && <circle cx={цт[0]} cy={цт[1]} r={5} className="fill-el" />}
        </g>
        {подвес && <line x1={ПОДВЕС[0]} y1={ПОДВЕС[1]} x2={ПОДВЕС[0]} y2={292} className="ink-muted" strokeWidth={1} />}
        {подвес && <circle cx={ПОДВЕС[0]} cy={292} r={4} className="fill-muted" />}
      </svg>

      <div className="lab-caption">
        {отверстие === null
          ? 'Нажми на отверстие — пластину подвесят за него.'
          : найден
            ? 'Отвесы пересеклись в одной точке — это центр тяжести.'
            : 'Отвес прошёл через центр тяжести. Подвесь за другое отверстие.'}
      </div>

      <Predict
        question={ф.вопрос}
        options={ф.ответы}
        right={ф.верный}
        revealed={найден}
        resetKey={ф.имя}
        explain={ф.пояснение}
      />

      <div className="lab-sub">Пластина</div>
      <Choice options={ФИГУРЫ.map((x, i) => ({ value: i, label: x.имя }))} value={n} onChange={сменить} />
      <p className="muted small">
        Центр тяжести — точка, к которой приложена сила тяжести тела. Подвешенное тело
        поворачивается так, чтобы центр тяжести встал точно под точкой подвеса.
      </p>
    </div>
  )
}
